import React, { memo, useCallback, useState } from "react";
import { Accordion, Tabs, Tab, Pagination, Button } from "react-bootstrap";
import DashboardDropdown from "../StudentDashboard/Dropdown/dropdown"; // Make sure to import your component
import LazyLoad from "react-lazyload";
import "./styles.scss";
import EmptyScreen from "../EmptyScreen";
import { clearAll } from "../../store/practiceSet";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import BsClock from "../../constants/icons/Bs/BsClock";
import RxStopwatch from "../../constants/icons/Rx/RxStopwatch";
import { BsBook } from "../../constants/icons/Bs/BsBook";
import * as ExamSlice from "../../store/examSlice";
import { getExamAttemptById } from "../../utils/assessments";
import { toast } from "react-toastify";
import {getUserRole} from "../../store/userRole";

const PracticeAccordion = ({
  practiceAttempts,
  testAttempts,
  assessmentAttempts,
  filter,
  onFilterChange,
  activeTab = "practice",
  onTabChange,
  hasIeltsExams,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const dispatch = useDispatch();
  const roleOfTheUser = useSelector(getUserRole);
  const navigate = useNavigate();
  const filterAttempts = useCallback((attempts, filter, hasIelts) => {
    const now = new Date();
    return attempts.filter((attempt) => {
      const attemptDate = new Date(hasIelts ? attempt?.completedAt : attempt.createdAt);
      if (filter === "today") {
        return now.toDateString() === attemptDate.toDateString();
      } else if (filter === "week") {
        const weekAgo = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - 7
        );
        return attemptDate >= weekAgo;
      } else if (filter === "month") {
        const monthAgo = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          now.getDate()
        );
        return attemptDate >= monthAgo;
      }
      return true;
    });
  }, []);

  const paginate = (items) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const groupedPracticeAttempts = filterAttempts(practiceAttempts, filter);
  const groupedTestAttempts = filterAttempts(testAttempts, filter);
  const groupedAssessmentAttempts = filterAttempts(assessmentAttempts, filter, hasIeltsExams);

  const [viewAttemptLoading, setViewAttemptLoading] = useState(false);
  const [selectedAttempt, setSelectedAttempt] = useState("");

  const onViewSolutionHandler = async (activity) => {
    if(activity?.data?.type === 'speaking') {
        navigate(`${roleOfTheUser === "Student"
              ? "/student"
              : ""}/myClassrooms/${activity?.classroomId}/${activity?.subjectId}/${activity?.topicId?._id}/${activeTab === 'practice' ? 'practice' : 'test'}/${activeTab === 'practice' ? 'speakingAttempt' : 'speakingTestAttempt' }/${activity?.examId}?attemptId=${activity?._id}`)
      return
    }
    if(activity?.data?.type === 'writing') {
        navigate(`${roleOfTheUser === "Student"
              ? "/student"
              : ""}/myClassrooms/${activity?.classroomId}/${activity?.subjectId}/${activity?.topicId?._id}/${activeTab === 'practice' ? 'practice' : 'test'}/${activeTab === 'practice' ? 'writingAttempt' : 'writingTestAttempt' }/${activity?.examId}?attemptId=${activity?._id}`)
      return
    }
    // /student/myClassrooms/:id/:subjectId/:topicId/test/writingTestAttempt/:testId/
    if(hasIeltsExams && !!activity?.attemptType) {
      // For IELTS, store attempt ID in sessionStorage and navigate to results
      sessionStorage.setItem(
        "fullExamAttemptId",
          activity?._id,
      );
      setViewAttemptLoading(true);
      setTimeout(() => {
        navigate(
          `${
            roleOfTheUser === "Student"
              ? "/student"
              : ""
          }/myClassrooms/${activity?.classroomId}/ielts-exams/${
            activity?.examId?._id
          }/attempt/results`,
        );
      }, 500);
      return
    }
    dispatch(clearAll());
    if (activity?.examType === "assessment") {
      setSelectedAttempt(activity?._id);
      setViewAttemptLoading(true);
      try {
        const resp = await getExamAttemptById(activity?._id);
        if (resp?.examAttempt) {
          dispatch(
            ExamSlice.setExamData({
              ...JSON.parse(resp?.examAttempt?.data),
              updatedAt: activity?.updatedAt,
              urlData: JSON.parse(resp?.examAttempt?.url),
            })
          );
          navigate(
            `/student/${activity?.classroomId}/exams/${activity?._id}/result?attemptId=${activity?._id}`
          );
        } else {
          toast.error("Failed to fetch exam attempt data");
        }
      } catch (err) {
        toast.error("Failed to fetch exam attempt data");
      } finally {
        setViewAttemptLoading(false);
      }
    } else {
      navigate(
        `/student/myClassrooms/${activity?.classroomId}/${activity?.subjectId}/${activity?.topicId?._id}/${activeTab}/${activity?._id}/${activity?.examId}`
      );
    }
  };

  const renderAccordion = (groupedAttempts, hasIelts) => {
    return(
    <>
      {groupedAttempts.length === 0 ? (
        <EmptyScreen
          heading="No Attempts Available"
          subHeading={`Please attempt ${activeTab} to view your progress`}
        />
      ) : (
        paginate(groupedAttempts).map((attempt, index) => (
          <Accordion className="mt-3" key={index}>
            <Accordion.Item eventKey={index.toString()}>
              <Accordion.Header className="d-flex gap-3">
                <LazyLoad height={20} offset={100}>
                  <img src="/CalendarBlank.png" alt="calendar" />
                </LazyLoad>
                <span>
                  {new Date(hasIelts ? attempt.completedAt : attempt.createdAt).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                    weekday: "long",
                  })}
                </span>
              </Accordion.Header>
              <Accordion.Body>
                <div className="d-flex justify-content-between flex-wrap">
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex align-items-center gap-4">
                      <h6>{hasIelts ? attempt?.examId?.title : attempt.name}</h6>
                      {attempt.examType !== "assessment" && (
                        <span className="badge bg-primary">
                          {attempt.topicId?.name || attempt.subjectName}
                        </span>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-4">
                      <div className="d-flex align-items-center gap-2">
                        <BsClock size={16} />
                        <span>
                          {new Date(hasIelts ? attempt.completedAt : (attempt.createdAt)).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <RxStopwatch size={16} />
                        <span>
                          {Math.floor((hasIelts ? attempt?.totalTimeSpent : attempt.timeSpent) / 60)}m{" "}
                          {(hasIelts ? attempt?.totalTimeSpent : attempt.timeSpent) % 60}s
                        </span>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-4">
                      {attempt.examType !== "assessment" && !attempt?.attemptType && (
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="progress"
                            style={{ width: "100px", height: "6px" }}
                          >
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{
                                width: attempt?.data?.type === 'writing' || attempt?.data?.type === 'speaking' ? `${(((attempt?.score || 0)/9)*100)}%` : `${
                                  (attempt?.correct / attempt?.totalQuestions) *
                                  100
                                }%`,
                                backgroundColor: "#23BD33",
                              }}
                            />
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{
                                width: attempt?.data?.type === 'writing' || attempt?.data?.type === 'speaking' ? `${(((9 - (attempt?.score || 0))/9)*100)}%` : `${
                                  ((attempt?.totalQuestions - attempt.correct) /
                                    attempt?.totalQuestions) *
                                  100
                                }%`,
                                backgroundColor: "#ff0000",
                              }}
                            />
                          </div>
                          {attempt?.data?.type === 'writing' || attempt?.data?.type === 'speaking' ?
                            <span>Band: {attempt?.score ?? 'Pending'}</span>
                          :
                            <span>
                              {attempt.correct}/{attempt?.totalQuestions} Correct
                            </span>
                          }
                        </div>
                      )}
                      {attempt.examType === "assessment" && !hasIelts && (
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-info">
                            {(() => {
                              try {
                                const urlData = JSON.parse(attempt.url);
                                return `${
                                  urlData.combinedSATScore ||
                                  attempt.obtainedPoints
                                }/${attempt.totalPoints} Points`;
                              } catch (e) {
                                return `${attempt.obtainedPoints}/${attempt.totalPoints} Points`;
                              }
                            })()}
                          </span>
                        </div>
                      )}
                    </div>
                    {(attempt.examType === "assessment" || hasIelts) && (
                      <div className="d-flex align-items-center gap-2">
                        <BsBook size={16} />
                        <span>{hasIelts ? attempt?.examId?.courseId?.name : attempt.courseId?.name}</span>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="link"
                    className="play-button"
                    onClick={() => {
                      onViewSolutionHandler(attempt);
                    }}
                  >
                    {viewAttemptLoading && attempt?._id === selectedAttempt ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                    ) : (
                      "View Details"
                    )}
                  </Button>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        ))
      )}
    </>
  )};

  return (
    <>
      <div
        className="dashboardThisWeek d-flex align-items-center justify-content-end w-100 flex-wrap"
        style={{ gap: "8px" }}
      >
        <span>{filter.charAt(0).toUpperCase() + filter.slice(1)}</span>
        <DashboardDropdown onItemSelect={onFilterChange} />
      </div>

      <Tabs
        id="controlled-tab-example"
        activeKey={activeTab}
        onSelect={(k) => {
          onTabChange(k);
          setCurrentPage(1);
        }}
      >
        <Tab eventKey="practice" title="Practice">
          {renderAccordion(groupedPracticeAttempts)}
          <Pagination className="mt-3 justify-content-center flex-wrap">
            {Array.from(
              {
                length: Math.ceil(
                  groupedPracticeAttempts.length / itemsPerPage
                ),
              },
              (_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === currentPage}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              )
            )}
          </Pagination>
        </Tab>
        <Tab eventKey="test" title="Test">
          {renderAccordion(groupedTestAttempts)}
          <Pagination className="mt-3 justify-content-center flex-wrap">
            {Array.from(
              { length: Math.ceil(groupedTestAttempts.length / itemsPerPage) },
              (_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === currentPage}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              )
            )}
          </Pagination>
        </Tab>
        <Tab eventKey="assessment" title="Assessment">
          {renderAccordion(groupedAssessmentAttempts, hasIeltsExams)}
          <Pagination className="mt-3 justify-content-center flex-wrap">
            {Array.from(
              {
                length: Math.ceil(
                  groupedAssessmentAttempts.length / itemsPerPage
                ),
              },
              (_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === currentPage}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              )
            )}
          </Pagination>
        </Tab>
      </Tabs>
    </>
  );
};

export default memo(PracticeAccordion);
